// Public surface every backend client must implement so RecCache and
// vue-wob-cache can drive it without knowing whether it speaks ADT or PCX.
//
// Spec: docs/wob-client-base.md
// Plan: docs/REC-API-MIGRATION.md
//
// Subclasses (AdtClient today, PcxClient later) override the abstract methods.
// The base throws so accidentally-missing overrides surface immediately
// instead of silently returning undefined.

function notImplemented(name) {
  return () => {
    throw new Error(`WobClientBase.${name} must be implemented by a subclass`)
  }
}

class WobClientBase {
  constructor() {
    // Auth + session state. Mutated directly by vue-wob-cache today; step 4
    // of the migration will replace these with a setSession() method.
    this.isAuthenticated = null
    this.user = {}
    this.accessToken = null

    // Token-refresh callback assigned by the auth layer.
    // Returns Promise<string | null>. Called before requests so the client
    // can use a freshly-refreshed token without round-tripping through the
    // plugin layer.
    this.tokenProvider = null
  }

  // One-time setup hook, called when RecCache.startSync() is first invoked.
  // Default is a no-op — override only if the backend needs lazy init.
  init() {}

  // Change the backend host. Implementations must invalidate any
  // change-detection state when the origin actually changes.
  setOrigin(/* origin */) {
    notImplemented('setOrigin')()
  }

  // Replace the bearer token. Passing null clears it. Implementations should
  // abort any in-flight read cycle so stale responses are discarded.
  setToken(/* token */) {
    notImplemented('setToken')()
  }

  // Atomic session update — replaces the four-step mutation that
  // vue-wob-cache used to do (isAuthenticated / user / accessToken / setToken)
  // and ensures the four fields can't drift relative to each other. The base
  // implementation delegates to setToken() so subclasses don't need to
  // override unless they carry extra session shape.
  setSession({ token = null, user = {}, isAuthenticated = false } = {}) {
    this.user = user
    this.isAuthenticated = isAuthenticated
    this.accessToken = token
    this.setToken(token)
  }

  // Tear down the session. Equivalent to setSession() with no arguments.
  clearSession() {
    this.setSession()
  }

  // Initiate a full sync cycle. When isCacheHydrated is false the client
  // must clear its change-detection state (no in-memory data for the filter
  // to protect).
  //
  // Dispatches: IndividualRefreshEvent, SyncProgressEvent,
  // SyncSkippedIdsEvent, IndividualDeleteEvent (for stale entries),
  // CacheSyncCompleteEvent.
  async readAllRequest(/* { isCacheHydrated } = {} */) {
    notImplemented('readAllRequest')()
  }

  // Re-read a single twin. Dispatches IndividualRefreshEvent.
  async readIndividualRequest(/* id */) {
    notImplemented('readIndividualRequest')()
  }

  // Create a new twin of `type` (an Iri). Returns the new twin id.
  async createRequest(/* type, existingId */) {
    notImplemented('createRequest')()
  }

  // Apply property + relationship diff inferred from JSON-LD.
  // Dispatches IndividualRefreshEvent.
  async updateRequest(/* jsonld */) {
    notImplemented('updateRequest')()
  }

  // Delete a twin and its incoming relationships.
  // Dispatches IndividualDeleteEvent.
  async deleteRequest(/* id */) {
    notImplemented('deleteRequest')()
  }

  // Transactional delete + recreate-with-new-type. Must be journalled so
  // recoverChangeTypeTransaction() can replay or roll back after a crash.
  async changeTypeRequest(/* id, newType */) {
    notImplemented('changeTypeRequest')()
  }

  // Replay or roll back a half-finished changeTypeRequest at app startup.
  // Returns null if no journal exists.
  async recoverChangeTypeTransaction() {
    return null
  }

  // Drop the entire change-detection cache (login/logout, env switch).
  clearEtagMap() {
    notImplemented('clearEtagMap')()
  }

  // Drop change-detection state for one or many ids so the next sync
  // re-reads them. Accepts a single id or an array; empty input is a no-op.
  forgetChangeDetection(/* ids */) {
    notImplemented('forgetChangeDetection')()
  }

  // Constrain change-detection state to the given ids, dropping everything
  // else. Called after the cache hydrates from IndexedDB so a "skip" can
  // never hide an individual the cache does not actually hold.
  //
  // Change-detection state is persisted by the cache, in the same IndexedDB
  // transaction as the individuals it describes — see the three methods
  // below. The two can still disagree when a save lands while the last
  // batches of a sync are being dispatched, so this remains the
  // reconciliation point. Backends whose change detection is not per-id
  // (e.g. per-class ETags) should drop everything rather than attempt a
  // partial retain.
  retainChangeDetection(/* ids */) {
    notImplemented('retainChangeDetection')()
  }

  // The client's change-detection state, for the cache to persist beside the
  // individuals. Opaque to the cache. No-op here rather than notImplemented:
  // a backend without change detection has nothing to persist, and the cache
  // calls this on every save.
  getChangeDetectionState() {
    return undefined
  }

  // Receive the state the cache persisted, after it has hydrated the
  // individuals that state describes. No-op for backends without it.
  restoreChangeDetection(/* state */) {}

  // The cache has durably persisted the state; the client may drop any copy
  // it keeps elsewhere. No-op for backends without one.
  changeDetectionPersisted() {}
}

export { WobClientBase }
